import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/App";

export const ABFA_CREST = "https://customer-assets.emergentagent.com/job_soccer-pro-21/artifacts/6hs5jyaw_image-removebg-preview.png";
const WS_BASE = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
const STALE_MS = 25000;

// Live feed for a screen target ("stadium", "screen-xxx" or "match-<id>").
// WebSocket push with exponential-backoff reconnect; HTTP polling fallback while the socket is down.
export const useScreenFeed = (target) => {
  const [snap, setSnap] = useState(null);
  const [status, setStatus] = useState("connecting"); // live | reconnecting | offline | connecting
  const [lastMessageAt, setLastMessageAt] = useState(null);
  const [error, setError] = useState(null);
  const skewRef = useRef(0);
  const versionRef = useRef(null);
  const clockRef = useRef(null);
  const lastAtRef = useRef(null);

  useEffect(() => {
    if (!target) return undefined;
    let ws = null;
    let closed = false;
    let retry = 0;
    let retryTimer = null;
    let pollTimer = null;

    const accept = (data) => {
      if (data.server_time) skewRef.current = new Date(data.server_time).getTime() - Date.now();
      lastAtRef.current = Date.now();
      setLastMessageAt(lastAtRef.current);
      if (data.type === "error") { setError(data.detail); return; }
      if (data.type === "heartbeat") {
        if (data.clock) clockRef.current = data.clock;
        return;
      }
      setError(null);
      // Ignore out-of-order / duplicate snapshots
      if (data.version && data.version === versionRef.current) return;
      versionRef.current = data.version;
      setSnap(data);
    };

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/big-screen/state/${target}`);
        accept({ type: "snapshot", ...res.data });
      } catch (e) {
        if (e.response?.status === 404) setError(e.response.data?.detail || "Screen not found");
      }
    };

    const startPolling = () => {
      if (pollTimer) return;
      poll();
      pollTimer = setInterval(poll, 5000);
    };
    const stopPolling = () => { clearInterval(pollTimer); pollTimer = null; };

    const connect = () => {
      if (closed) return;
      try {
        ws = new WebSocket(`${WS_BASE}/api/big-screen/ws/${target}`);
      } catch {
        scheduleRetry();
        return;
      }
      ws.onopen = () => { retry = 0; setStatus("live"); stopPolling(); };
      ws.onmessage = (ev) => { try { accept(JSON.parse(ev.data)); } catch { /* ignore malformed */ } };
      ws.onclose = () => { if (!closed) scheduleRetry(); };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };

    const scheduleRetry = () => {
      setStatus(retry > 3 ? "offline" : "reconnecting");
      startPolling();
      const delay = Math.min(15000, 1000 * 2 ** retry);
      retry += 1;
      clearTimeout(retryTimer);
      retryTimer = setTimeout(connect, delay);
    };

    // Watchdog: half-open sockets (silent network loss) → force reconnect + polling
    const watchdog = setInterval(() => {
      if (ws && ws.readyState === WebSocket.OPEN && lastAtRef.current && Date.now() - lastAtRef.current > STALE_MS) {
        ws.onclose = null;
        try { ws.close(); } catch { /* noop */ }
        scheduleRetry();
      }
    }, 5000);

    connect();
    const onOnline = () => { retry = 0; clearTimeout(retryTimer); try { ws?.close(); } catch { /* noop */ } connect(); };
    window.addEventListener("online", onOnline);
    return () => {
      closed = true;
      clearInterval(watchdog);
      window.removeEventListener("online", onOnline);
      clearTimeout(retryTimer);
      stopPolling();
      try { ws?.close(); } catch { /* noop */ }
    };
  }, [target]);

  const stale = lastMessageAt ? Date.now() - lastMessageAt > STALE_MS : false;
  return { snap, status, stale, lastMessageAt, error, skewMs: skewRef };
};

// ---------- clock ----------
const PERIODS = {
  first_half: { offset: 0, cap: 45, label: "First Half" },
  second_half: { offset: 45, cap: 45, label: "Second Half" },
  extra_time_first: { offset: 90, cap: 15, label: "Extra Time · 1st" },
  extra_time_second: { offset: 105, cap: 15, label: "Extra Time · 2nd" },
};
const STATE_LABELS = {
  pre_match: "Kick-off", half_time: "Half-Time", extra_time_half: "Extra Time · Break", shootout: "Penalty Shootout",
  full_time: "Full-Time", paused: "Clock Paused", abandoned: "Abandoned", suspended: "Suspended",
};
const pad = (n) => String(n).padStart(2, "0");

// Returns { main: "67:24", added: "+2:13" | null, period: "Second Half", running }
export const screenClock = (clock, match, skewMs) => {
  const status = match?.status;
  if (status && ["postponed", "suspended", "abandoned", "cancelled"].includes(status)) {
    return { main: status.toUpperCase(), added: null, period: "Match " + status, running: false, special: true };
  }
  if (!clock) return { main: "--:--", added: null, period: "", running: false };
  const state = clock.clock_state === "paused" ? clock.prev_clock_state : clock.clock_state;
  const p = PERIODS[state];
  if (!p) {
    return { main: state === "full_time" ? "FT" : state === "half_time" ? "HT" : state === "shootout" ? "PENS" : match?.match_time || "--:--",
      added: null, period: STATE_LABELS[clock.clock_state] || "", running: false, special: true };
  }
  let elapsed = clock.period_elapsed_seconds || 0;
  if (clock.is_running && clock.period_started_at) {
    elapsed = Math.max(0, Math.floor((Date.now() + skewMs - new Date(clock.period_started_at).getTime()) / 1000));
  }
  const capS = p.cap * 60;
  const base = Math.min(elapsed, capS) + p.offset * 60;
  const extra = elapsed > capS ? elapsed - capS : 0;
  return {
    main: `${pad(Math.floor(base / 60))}:${pad(base % 60)}`,
    added: extra ? `+${Math.floor(extra / 60)}:${pad(extra % 60)}` : null,
    period: clock.clock_state === "paused" ? "Clock Paused" : p.label,
    running: !!clock.is_running,
  };
};

export const isLive = (match, clock) =>
  !!clock?.is_running || ["half_time", "extra_time_half", "shootout", "paused"].includes(clock?.clock_state) || match?.status === "live";

export const GOAL_TYPES = ["goal", "penalty_goal", "own_goal"];

// Team credited with a goal: own goals count for the opponent
export const goalSide = (ev, match) => {
  const scorerIsHome = ev.team_id === match.home_team_id;
  return ev.type === "own_goal" ? (scorerIsHome ? "away" : "home") : (scorerIsHome ? "home" : "away");
};

export const minuteLabel = (ev) => (ev.minute == null ? "" : `${ev.minute}${ev.added_time ? `+${ev.added_time}` : ""}'`);
